/**
 * A soft two-tone gong for reminders on the wall display (1.13) – synthesised, no sound file.
 * Each device decides for itself (Einstellungen → Dieses Gerät): off, soft, medium, loud.
 * Browsers only allow sound after the first touch; the wall has usually been touched.
 */
export type GongLevel = 'off' | 'soft' | 'medium' | 'loud';
const KEY = 'shh.gong';
const GAIN: Record<Exclude<GongLevel, 'off'>, number> = { soft: 0.08, medium: 0.18, loud: 0.35 };

export function getGong(): GongLevel {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'off' || v === 'soft' || v === 'loud' ? v : 'medium';
  } catch {
    return 'medium';
  }
}
export function setGong(level: GongLevel) {
  try {
    localStorage.setItem(KEY, level);
  } catch {
    /* private mode: this session only */
  }
}

let ctx: AudioContext | undefined;
/** Unlock audio on the first touch (browser rule) so a later reminder can sound. */
export function primeGong() {
  if (typeof window === 'undefined' || ctx) return;
  const unlock = () => {
    ctx ??= new AudioContext();
    void ctx.resume();
  };
  window.addEventListener('pointerdown', unlock, { once: true, passive: true });
}

export function playGong(level = getGong()) {
  if (level === 'off') return;
  try {
    ctx ??= new AudioContext();
    void ctx.resume();
    const now = ctx.currentTime;
    // two soft bell tones, a fifth apart, fading out
    [
      [660, 0],
      [990, 0.35],
    ].forEach(([freq, at]) => {
      const osc = ctx!.createOscillator();
      const gain = ctx!.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq!;
      gain.gain.setValueAtTime(0, now + at!);
      gain.gain.linearRampToValueAtTime(GAIN[level], now + at! + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at! + 1.6);
      osc.connect(gain).connect(ctx!.destination);
      osc.start(now + at!);
      osc.stop(now + at! + 1.7);
    });
  } catch {
    // no audio on this device – the card is still there
  }
}
